/**
 * Utilitários compartilhados de UI de validação dos formulários do
 * `OfertaFormularioModule` (modelos `padrao`, `form-no-cep` e `form-2-steps`).
 *
 * Contrato único de erro de campo (SDD "validacao-sistemica-formularios", AC-13):
 * - botão de envio nunca nasce `disabled` por validade — a validação acontece no
 *   clique e no blur;
 * - toda mensagem de validação de campo é escrita por {@link setFieldError}, que
 *   aplica o prefixo `⚠️ ` (AC-10), `role="alert"` na mensagem e
 *   `aria-invalid`/`aria-describedby` no controle pareado;
 * - um submit inválido marca **todos** os campos inválidos (sem early return) e
 *   foca o primeiro deles na ordem visual via {@link focusFirstInvalidField};
 * - cada checkbox de opt-in desmarcado recebe a **sua própria** mensagem (AC-9),
 *   nunca um bloqueio silencioso.
 *
 * Convenção de ids (já existente no projeto): todo id/classe é prefixado por
 * `cssPrefix`. Para um campo de id lógico `fieldId`:
 * - controle: `#${cssPrefix}-${fieldId}`
 * - mensagem: `#${cssPrefix}-${fieldId}-error` (div `.${cssPrefix}-error`)
 *
 * Todas as funções operam dentro do `shadowRoot` do widget — nunca em `document`.
 */

/** Prefixo visual obrigatório de toda mensagem de validação de campo (AC-10). */
export const ERROR_PREFIX = '⚠️ ';

/**
 * Mensagem por checkbox de consentimento, indexada pelo atributo `name` do
 * input (AC-9 / D5). `opt_in_aceite` é o aceite de comunicações — usa a redação
 * "para continuar"; os demais são aceite de termos/política.
 * @type {Record<string, string>}
 */
export const OPTIN_MESSAGES = {
    opt_in_widget: 'Marque o campo de aceite dos termos',
    opt_in_ciente: 'Marque o campo de aceite dos termos',
    opt_in_declaro: 'Marque o campo de aceite dos termos',
    opt_in_aceite: 'Marque o campo de aceite para continuar',
};

/** Fallback para um opt-in novo que ainda não esteja mapeado em OPTIN_MESSAGES. */
export const DEFAULT_OPTIN_MESSAGE = 'Marque o campo de aceite dos termos';

/**
 * Regra de e-mail única dos modelos do `OfertaFormularioModule` — estava
 * duplicada byte a byte nos três handlers (`padrao`, `form-no-cep`,
 * `form-2-steps`).
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Telefone **já mascarado** por `maskTelefone`: `(11) 99999-9999`.
 * Mesma regra que os três handlers declaravam localmente.
 * @param {string} [phoneValue]
 * @returns {boolean}
 */
export function isPhoneValid(phoneValue) {
    return /^\(\d{2}\)\s\d{5}-\d{4}$/.test(phoneValue || '');
}

/** Mensagens canônicas de e-mail/telefone (sem o prefixo `⚠️ `, que `setFieldError` aplica). */
export const EMAIL_REQUIRED_MESSAGE = 'E-mail é obrigatório.';
export const EMAIL_INVALID_MESSAGE = 'E-mail inválido.';
export const PHONE_REQUIRED_MESSAGE = 'Telefone é obrigatório.';
export const PHONE_INVALID_MESSAGE = 'Preencha o telefone corretamente.';

/**
 * Deriva o `fieldId` lógico de um controle a partir do seu `id` no DOM,
 * removendo o `cssPrefix`. Ex.: `mzk-123-aceite` → `aceite`.
 * @param {Element} element
 * @param {string} cssPrefix
 * @returns {string}
 */
export function fieldIdFromElement(element, cssPrefix) {
    const domId = element?.id || '';
    const prefix = `${cssPrefix}-`;
    if (domId.startsWith(prefix)) {
        return domId.slice(prefix.length);
    }
    return domId || element?.name || '';
}

/**
 * Escreve (ou limpa) a mensagem de validação de um campo, aplicando o contrato
 * canônico de erro: prefixo `⚠️ `, `role="alert"` na div de mensagem e
 * `aria-invalid`/`aria-describedby` no controle pareado.
 *
 * Passar `message` vazio/nulo limpa a mensagem e remove os atributos ARIA.
 *
 * @param {ShadowRoot|DocumentFragment} shadowRoot Raiz de busca (Shadow DOM do widget).
 * @param {string} cssPrefix Prefixo de ids/classes da instância do widget.
 * @param {string} fieldId Id lógico do campo (sufixo após `${cssPrefix}-`).
 * @param {string} [message] Mensagem SEM o prefixo `⚠️ `; vazio limpa o erro.
 * @param {{control?: string}} [options] `control`: sufixo do id do controle a
 *   receber os atributos ARIA, quando diferente de `fieldId` (ex.: o multiselect
 *   de modelos, cuja div de erro é `modelos-interesse` e o controle focável é
 *   `modelos-trigger`).
 * @returns {void}
 */
export function setFieldError(shadowRoot, cssPrefix, fieldId, message, options = {}) {
    if (!shadowRoot || !fieldId) return;

    const { control } = options;
    const errorElement = shadowRoot.querySelector(`#${cssPrefix}-${fieldId}-error`);
    const controlElement = shadowRoot.querySelector(`#${cssPrefix}-${control || fieldId}`);
    const text = message ? `${ERROR_PREFIX}${message}` : '';

    if (errorElement) {
        errorElement.textContent = text;
        errorElement.setAttribute('role', 'alert');
        errorElement.setAttribute('aria-live', 'polite');
        errorElement.classList.toggle('show', Boolean(text));
    }

    if (controlElement) {
        if (text) {
            controlElement.setAttribute('aria-invalid', 'true');
            if (errorElement) {
                controlElement.setAttribute('aria-describedby', `${cssPrefix}-${fieldId}-error`);
            }
        } else {
            controlElement.removeAttribute('aria-invalid');
            controlElement.removeAttribute('aria-describedby');
        }
    }
}

/**
 * Versão "curried" de {@link setFieldError} para os handlers, que já conhecem
 * `shadowRoot` e `cssPrefix`. Existe para que os call sites continuem legíveis
 * (`setFieldError('cpf', 'CPF inválido.')`) **sem** nenhuma reimplementação
 * local da lógica de erro/ARIA.
 *
 * @param {ShadowRoot|DocumentFragment} shadowRoot
 * @param {string} cssPrefix
 * @returns {(fieldId: string, message?: string, options?: {control?: string}) => void}
 */
export function createFieldErrorSetter(shadowRoot, cssPrefix) {
    return (fieldId, message, options) =>
        setFieldError(shadowRoot, cssPrefix, fieldId, message, options);
}

/**
 * Indica se um campo está exibindo mensagem de erro no momento.
 * @param {ShadowRoot|DocumentFragment} shadowRoot
 * @param {string} cssPrefix
 * @param {string} fieldId
 * @returns {boolean}
 */
export function hasFieldError(shadowRoot, cssPrefix, fieldId) {
    const errorElement = shadowRoot?.querySelector(`#${cssPrefix}-${fieldId}-error`);
    return Boolean(errorElement && errorElement.textContent.trim());
}

/**
 * Valida o grupo de checkboxes de consentimento (LGPD), emitindo **uma mensagem
 * por checkbox desmarcado** (AC-9) em vez de bloquear o submit em silêncio.
 *
 * @param {ShadowRoot|DocumentFragment} shadowRoot
 * @param {string} cssPrefix
 * @param {{showErrors?: boolean, touched?: Set<string>|null, groupId?: string}} [options]
 *   - `showErrors`: força a exibição de todas as mensagens (tentativa de submit);
 *   - `touched`: conjunto de `name`s de checkbox já interagidos — só esses exibem
 *     mensagem fora de uma tentativa de submit;
 *   - `groupId`: sufixo do id do container do grupo (padrão: `checkboxes`).
 * @returns {boolean} `true` quando todos os opt-ins renderizados estão marcados.
 */
export function validateOptinCheckboxes(shadowRoot, cssPrefix, options = {}) {
    const { showErrors = false, touched = null, groupId = 'checkboxes' } = options;
    if (!shadowRoot) return true;

    const checkboxes = Array.from(
        shadowRoot.querySelectorAll(`#${cssPrefix}-${groupId} input[type="checkbox"]`)
    );

    let allChecked = true;

    checkboxes.forEach((checkbox) => {
        const fieldId = fieldIdFromElement(checkbox, cssPrefix);
        if (!fieldId) return;

        if (checkbox.checked) {
            setFieldError(shadowRoot, cssPrefix, fieldId, '');
            return;
        }

        allChecked = false;

        const shouldShow = showErrors || Boolean(touched && touched.has(checkbox.name));
        if (shouldShow) {
            const message = OPTIN_MESSAGES[checkbox.name] || DEFAULT_OPTIN_MESSAGE;
            setFieldError(shadowRoot, cssPrefix, fieldId, message);
        }
    });

    return allChecked;
}

/**
 * Foca (e rola até) o primeiro campo que está exibindo mensagem de erro,
 * seguindo a ordem visual declarada pelo renderer.
 *
 * Equivalente do `focusFirstInvalidField` do portal, operando dentro do Shadow
 * DOM: usa `focus({preventScroll:true})` seguido de
 * `scrollIntoView({block:"center", behavior:"smooth"})` para que o ajuste
 * aconteça em **todos** os ancestrais roláveis (página do host, modal, drawer)
 * sem o salto bruto do foco nativo.
 *
 * @param {ShadowRoot|DocumentFragment} shadowRoot
 * @param {string} cssPrefix
 * @param {ReadonlyArray<string|{field: string, control?: string}>} orderedFields
 *   Ordem visual dos campos. Cada entrada é o `fieldId` ou um par
 *   `{field, control}` quando o controle focável tem id diferente da div de erro.
 * @returns {boolean} `true` se algum campo foi focado.
 */
export function focusFirstInvalidField(shadowRoot, cssPrefix, orderedFields) {
    if (!shadowRoot || !Array.isArray(orderedFields)) return false;

    for (const entry of orderedFields) {
        const field = typeof entry === 'string' ? entry : entry?.field;
        if (!field) continue;

        const control = (typeof entry === 'string' ? null : entry?.control) || field;

        if (!hasFieldError(shadowRoot, cssPrefix, field)) continue;

        const controlElement = shadowRoot.querySelector(`#${cssPrefix}-${control}`);
        if (!controlElement || typeof controlElement.focus !== 'function') continue;

        try {
            controlElement.focus({ preventScroll: true });
        } catch {
            controlElement.focus();
        }

        if (typeof controlElement.scrollIntoView === 'function') {
            controlElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }

        return true;
    }

    return false;
}
